import { Outlet, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

import { useAuthState } from '@creditornot/wolt-auth/react';
import { AuthLoadingSpinner } from 'common/components/cookbook/AuthLoadingSpinner';
import { useFeaturesQuery } from 'common/feature-flag/query';
import { useWarpMerchant } from 'common/hooks/useWarpMerchant';
import { useWarpMerchantsForUser } from 'common/hooks/useWarpMerchantsForUser';
import { setReturnPath } from 'common/utils/returnPath';

export function AuthVerification() {
  const { status, user } = useAuthState();
  const navigate = useNavigate();
  const { merchant } = useWarpMerchant();

  // Wait until merchant is available to avoid 400 errors due to missing X-Warp-Merchant-Id header
  const { data, isFetching: featuresFetching } = useFeaturesQuery({
    enabled: status === 'logged-in' && !!merchant,
  });

  // start loading merchants early to avoid waterfalls
  const { isLoading: merchantsLoading } = useWarpMerchantsForUser(status === 'logged-in');

  const featuresLoading = !data && featuresFetching;

  const isLoadingState = status === 'logging-in' || featuresLoading || merchantsLoading;
  const isLoggedOutState = status === 'logged-out';
  const isLoggedInState = status === 'logged-in' && !!user;

  useEffect(() => {
    if (isLoggedOutState) {
      setReturnPath();
      void navigate({ to: '/login' });
    }
  }, [isLoggedOutState, navigate]);

  if (isLoadingState || isLoggedOutState) {
    return <AuthLoadingSpinner />;
  }

  if (isLoggedInState) {
    return <Outlet />;
  }

  console.error('[AuthVerification] Unexpected auth state:', {
    status,
    hasUser: !!user,
    isLoadingState,
    isLoggedInState,
    isLoggedOutState,
    featuresLoading,
    merchantsLoading,
  });
  return <AuthLoadingSpinner />;
}
